'use client';

import 'react-datepicker/dist/react-datepicker.css';
import { useCallback, useEffect, useState, useMemo, useTransition } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useRouter, usePathname } from 'next/navigation';
import DatePicker, { registerLocale } from 'react-datepicker';
import pl from 'date-fns/locale/pl';
import clsx from 'clsx';
import debounce from 'lodash.debounce';

import { RemoveButton } from './remove-button';
import { TreeComponent } from './tree-component';
import Loader from 'components/loader';

registerLocale('pl', pl);

const orders = [
  { id: 'ASC', name: 'Rosnąco' },
  { id: 'DESC', name: 'Malejąco' },
];

export default function SearchFiltersClient({ initialFilters, categoriesForTree, tagsEdges }) {
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();

  const [dateRange, setDateRange] = useState([
    initialFilters.startDate ? new Date(initialFilters.startDate) : null,
    initialFilters.endDate ? new Date(initialFilters.endDate) : null,
  ]);
  const [isCalendarIsOpen, setIsCalendarOpen] = useState(false);

  const { control, handleSubmit, watch, resetField, setValue, getValues } = useForm({
    defaultValues: {
      search: initialFilters.search || '',
      categoryIn: initialFilters.categoryIn || [],
      tagIn: initialFilters.tagIn || [],
      order: initialFilters.order || '',
      date: initialFilters.startDate ? [initialFilters.startDate, initialFilters.endDate] : [null, null],
    },
  });

  // Synchronizacja pola 'search', gdy użytkownik wpisze nową frazę w głównym nagłówku
  useEffect(() => {
    setValue('search', initialFilters.search || '');
  }, [initialFilters.search, setValue]);

  const watchShowDateLabel = watch('date', false);
  const calendarHasValue = watchShowDateLabel != null && watchShowDateLabel[0] != null;

  const updateUrlWithFilters = useCallback(
    (formData, targetPage = 1) => {
      const startDate =
        Array.isArray(formData.date) && formData.date[0] ? new Date(formData.date[0]).toISOString() : null;
      const endDate =
        Array.isArray(formData.date) && formData.date[1] ? new Date(formData.date[1]).toISOString() : null;

      const currentParams = new URLSearchParams();

      if (formData.search) currentParams.set('search', formData.search);
      if (formData.categoryIn?.length) formData.categoryIn.forEach((id) => currentParams.append('categoryIn', id));
      if (formData.tagIn?.length) formData.tagIn.forEach((id) => currentParams.append('tagIn', id));
      if (formData.order) currentParams.set('order', formData.order);
      if (startDate) currentParams.set('startDate', startDate);
      if (endDate) currentParams.set('endDate', endDate);
      if (targetPage > 1) currentParams.set('page', String(targetPage));

      const queryString = currentParams.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

      startTransition(() => {
        router.replace(newUrl, { scroll: false });
      });
    },
    [pathname, router],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((formData) => {
        updateUrlWithFilters(formData, 1);
      }, 500),
    [updateUrlWithFilters],
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const selectedCategoryIds = watch('categoryIn') || [];
  const selectedTagIds = watch('tagIn') || [];

  const tagsTree = useMemo(
    () =>
      tagsEdges.map((edge) => {
        const idAsNumber = edge.node.databaseId;
        return {
          id: idAsNumber,
          name: edge.node.name,
          value: idAsNumber,
          label: edge.node.name,
          children: edge.node.children,
          checked: selectedTagIds.includes(idAsNumber) || selectedTagIds.includes(String(idAsNumber)),
        };
      }),
    [tagsEdges, selectedTagIds],
  );

  const categoriesTree = useMemo(
    () =>
      categoriesForTree.map((edge) => {
        const idAsNumber = edge.node.databaseId;
        const parent = {
          value: idAsNumber,
          id: idAsNumber,
          name: edge.node.name,
          label: edge.node.name,
          expanded: true,
          checked: selectedCategoryIds.includes(idAsNumber) || selectedCategoryIds.includes(String(idAsNumber)),
        };

        if (Array.isArray(edge.node.children?.edges)) {
          parent.children = edge.node.children.edges.map((child) => {
            const childId = child.node.databaseId;
            return {
              id: childId,
              value: childId,
              name: child.node.name,
              label: child.node.name,
              checked: selectedCategoryIds.includes(childId) || selectedCategoryIds.includes(String(childId)),
            };
          });
        }
        return parent;
      }),
    [categoriesForTree, selectedCategoryIds],
  );

  return (
    <>
      <div className="searchFilters w-full px-0">
        <form onSubmit={handleSubmit((data) => updateUrlWithFilters(data, 1))} className="w-full px-0">
          <div className="grid grid-cols-2 gap-4 px-0 lg:grid-cols-4">
            {/* 1. DatePicker */}
            <div className="relative z-[2] px-0">
              <Controller
                control={control}
                name="date"
                render={({ field }) => (
                  <div
                    className={clsx(
                      'relative w-full rounded-[4px] border border-solid bg-[#1a1a1a] transition-colors',
                      isCalendarIsOpen || calendarHasValue ? 'border-[#10B7FF]' : 'border-[#444] hover:border-[#666]',
                    )}
                  >
                    <label
                      className={clsx(
                        'pointer-events-none absolute left-3 z-10 transition-all duration-200',
                        isCalendarIsOpen || calendarHasValue
                          ? '-top-[9px] bg-[#1a1a1a] px-1 text-[12px] text-[#10B7FF]'
                          : 'top-1/2 -translate-y-1/2 text-[14px] text-white',
                      )}
                      htmlFor="date"
                    >
                      Daty
                    </label>

                    <div className="relative flex h-[48px] items-center px-0">
                      <DatePicker
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                        onChange={(e) => {
                          setDateRange(e);
                          field.onChange(e);
                          if (e[0] !== null && e[1] !== null) {
                            debouncedSearch({ ...getValues(), date: e });
                          }
                        }}
                        selectsRange
                        value={field.value}
                        showMonthYearPicker
                        shouldCloseOnSelect
                        dateFormat="MM/yyyy"
                        locale="pl"
                        onCalendarClose={() => setIsCalendarOpen(false)}
                        onCalendarOpen={() => setIsCalendarOpen(true)}
                        className="w-full bg-transparent py-2 pl-3 pr-10 text-[14px] text-white focus:outline-none"
                      />

                      <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center justify-center px-0">
                        {calendarHasValue ? (
                          <RemoveButton
                            hasValue={calendarHasValue}
                            onClick={() => {
                              resetField('date');
                              setDateRange([null, null]);
                              debouncedSearch({ ...getValues(), date: [null, null] });
                            }}
                          />
                        ) : (
                          <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 5v14M5 12h14" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>

            {/* 2. CategoryTree */}
            <div className="px-0">
              <Controller
                name="categoryIn"
                control={control}
                render={({ field }) => (
                  <TreeComponent
                    data={categoriesTree}
                    id="search-categories-tree"
                    inputLabel="Kategorie"
                    name="categoryIn"
                    placeholder="Kategorie"
                    handleOnChange={(currentItem, allItems) => {
                      const categoryIds = Array.isArray(allItems) ? allItems.map((item) => Number(item.value)) : [];
                      field.onChange(categoryIds);
                      debouncedSearch({ ...getValues(), categoryIn: categoryIds });
                    }}
                  />
                )}
              />
            </div>

            {/* 3. TagTree */}
            <div className="px-0 h-[48px]">
              <Controller
                name="tagIn"
                control={control}

                render={({ field }) => (
                  <TreeComponent
                    id="search-tag-tree"
                    data={tagsTree}
                    inputLabel="Tagi"
                    name="tagIn"
                    placeholder="Tagi"
                    handleOnChange={(currentItem, allItems) => {
                      const tagsIds = Array.isArray(allItems) ? allItems.map((item) => Number(item.value)) : [];
                      field.onChange(tagsIds);
                      debouncedSearch({ ...getValues(), tagIn: tagsIds });
                    }}
                  />
                )}
              />
            </div>

            {/* 4. Sort */}
            <div className="px-0">
              <Controller
                name="order"
                control={control}
                render={({ field }) => (
                  <div
                    className={clsx(
                      'relative w-full rounded-[4px] border border-solid bg-[#1a1a1a] transition-colors',
                      field.value ? 'border-[#10B7FF]' : 'border-[#444] hover:border-[#666]',
                    )}
                  >
                    <label
                      className={clsx(
                        'pointer-events-none absolute left-3 z-10 transition-all duration-200',
                        field.value
                          ? '-top-[9px] bg-[#1a1a1a] px-1 text-[12px] text-[#10B7FF]'
                          : 'top-1/2 -translate-y-1/2 text-[14px] text-white',
                      )}
                      htmlFor="order"
                    >
                      Sortuj według
                    </label>

                    <div className="relative flex h-[48px] items-center px-0">
                      <select
                        {...field}
                        id="order"
                        className={clsx(
                          'w-full cursor-pointer appearance-none bg-transparent py-2 pl-3 pr-10 text-[14px] outline-none',
                          field.value ? 'text-white' : 'text-transparent',
                        )}
                        onChange={(e) => {
                          field.onChange(e);
                          debouncedSearch({ ...getValues(), order: e.target.value });
                        }}
                      >
                        <option value="" className="hidden"></option>
                        {orders.map((order) => (
                          <option value={order.id} key={order.id} className="bg-[#1a1a1a] text-white">
                            {order.name}
                          </option>
                        ))}
                      </select>

                      <div className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center px-0">
                        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>

          <button hidden={true} type="submit" />

          {/* 5. Ukryty Search Input */}
          <Controller name="search" control={control} render={({ field }) => <input type="hidden" {...field} />} />
        </form>
      </div>

      {isPending && <Loader show={true} />}
    </>
  );
}
